import { useCallback, useRef, useState } from 'react';
import { useDiagramStore } from '../stores/diagramStore';
import { snapshotFromStore } from '../stores/undoStore';
import type { Node } from '../types';
import { collectStackDescendantIds } from '../utils/drawStack';

type CheckFrameMembership = (nodeId: string, pos: { x: number; y: number }, sz: { width: number; height: number }) => void;

export function useMultiDrag(
  selectedItems: string[],
  nodes: Node[],
  updateNode: (id: string, updates: Partial<Node>) => void,
  checkFrameMembership: CheckFrameMembership,
  checkStackMembership?: (nodeId: string) => void,
) {
  const multiDragOrigins = useRef<Map<string, { x: number; y: number }> | null>(null);
  const [dragOverridePos, setDragOverridePos] = useState<Record<string, { x: number; y: number }>>({});

  const handleMultiDragStart = useCallback((draggedId: string) => {
    const dragged = nodes.find(n => n.id === draggedId);
    if (!dragged) {
      multiDragOrigins.current = null;
      return;
    }
    const origins = new Map<string, { x: number; y: number }>();
    origins.set(draggedId, { x: dragged.position.x, y: dragged.position.y });

    const selectedNodeIds = selectedItems.filter(id => nodes.some(n => n.id === id));
    const multi = selectedNodeIds.length >= 2 && selectedNodeIds.includes(draggedId);
    if (multi) {
      selectedNodeIds.forEach(id => {
        const node = nodes.find(n => n.id === id);
        if (node) origins.set(id, { x: node.position.x, y: node.position.y });
      });
    }

    for (const originId of [...origins.keys()]) {
      for (const childId of collectStackDescendantIds(originId, nodes)) {
        if (origins.has(childId)) continue;
        const child = nodes.find(n => n.id === childId);
        if (child) origins.set(childId, { x: child.position.x, y: child.position.y });
      }
    }

    if (origins.size > 1) snapshotFromStore();
    multiDragOrigins.current = origins;
  }, [selectedItems, nodes]);

  const handleMultiDragMove = useCallback((draggedId: string, dx: number, dy: number) => {
    if (!multiDragOrigins.current) return;
    multiDragOrigins.current.forEach((origin, id) => {
      if (id === draggedId) {
        setDragOverridePos(prev => ({ ...prev, [id]: { x: origin.x + dx, y: origin.y + dy } }));
      } else {
        updateNode(id, { position: { x: origin.x + dx, y: origin.y + dy } });
      }
    });
  }, [updateNode]);

  const handleMultiDragEnd = useCallback((draggedId: string, dx: number, dy: number) => {
    if (!multiDragOrigins.current) return;
    const origins = multiDragOrigins.current;
    origins.forEach((origin, id) => {
      if (id === draggedId) return;
      updateNode(id, { position: { x: origin.x + dx, y: origin.y + dy } });
    });
    setDragOverridePos({});
    multiDragOrigins.current = null;
    origins.forEach((origin, id) => {
      const node = useDiagramStore.getState().nodes.find(n => n.id === id);
      if (!node) return;
      const pos = id === draggedId
        ? node.position
        : { x: origin.x + dx, y: origin.y + dy };
      checkFrameMembership(id, pos, node.size);
      checkStackMembership?.(id);
    });
  }, [updateNode, checkFrameMembership, checkStackMembership]);

  return { dragOverridePos, handleMultiDragStart, handleMultiDragMove, handleMultiDragEnd };
}
